<script setup lang="ts">
import 'mapbox-gl/dist/mapbox-gl.css';
import Mapbox from 'mapbox-gl';
import type { LngLatBounds } from 'mapbox-gl';
import { type LgnLat, MAPBOX_PROVIDE_INJECT, type UnsafeLgnLat } from '~/composables/mapbox/map';
import { IAD_PARIS, useSafePosition } from '~/composables/mapbox/map';

// NOTE: We don't use Vue Map component as it doesn't work
// • https://github.com/vinayakkulkarni/v-mapbox
// • https://github.com/vinayakkulkarni/v-mapbox/issues/1701

defineOptions({
  // We don't want attributes to be passed down to <slot /> or wrapper div
  // Only to where the Map will be rendered so we can specify easily height and so on
  inheritAttrs: false,
});

const props = withDefaults(
  defineProps<{
    containerId?: string
    center?: UnsafeLgnLat
  }>(),
  {
    containerId: `iad-mapbox-map`,
    center: () => IAD_PARIS,
  },
);

const emit = defineEmits<{
  'update:center': [LgnLat]
  'update:bounds': [LngLatBounds]
}>();

defineSlots<{
  default: () => unknown
}>();

const runtimeConfig = useRuntimeConfig();
const accessToken = runtimeConfig.public.mapboxToken;
const centerRef = toRef(props, `center`);
const { position: safePosition } = useSafePosition(centerRef);
const options = computed<mapboxgl.MapOptions>(() => ({
  accessToken,
  container: props.containerId, // container ID
  center: safePosition.value, // starting position [lng, lat]
  zoom: 15, // starting zoom
  maxZoom: 18,
  style: `mapbox://styles/mapbox/streets-v12?optimize=true`, // style URL or style object
  crossSourceCollisions: false,
  failIfMajorPerformanceCaveat: false,
  attributionControl: false,
  hash: false,
}));

const mapbox = shallowRef<mapboxgl.Map>();
// Allows nesting Mapbox components by giving children a way to hook into Mapbox instance
provide(MAPBOX_PROVIDE_INJECT, { mapbox });

// Wait for the <div> to be rendered
onMounted(() => {
  // https://docs.mapbox.com/mapbox-gl-js/api/map/
  mapbox.value = new Mapbox.Map(options.value);
  updateCoordinates();
  mapbox.value.on(`move`, updateCoordinates);
  mapbox.value.on(`moveend`, updateCoordinates);
});

onBeforeUnmount(() => {
  if (!mapbox.value) return;
  mapbox.value.remove();
  mapbox.value = undefined;
});

function updateCoordinates() {
  if (!mapbox.value) return;
  const center = mapbox.value.getCenter();
  emit(`update:center`, [center.lng, center.lat]);
  const bounds = mapbox.value.getBounds();
  if (!bounds) return;
  emit(`update:bounds`, bounds);
}

watch(safePosition, () => {
  if (!mapbox.value) return;
  const center = mapbox.value.getCenter();
  if (safePosition.value.join(`|`) === [center.lng, center.lat].join(`|`)) return;
  mapbox.value.setCenter(safePosition.value);
});
</script>

<template>
  <div class="contents">
    <div
      :id="containerId"
      v-bind="$attrs"
    />
    <slot />
  </div>
</template>
